/**
 * ClientsMobile — мобильная раскладка «Клиенты» (design 02_clients, экран A):
 * хедер (счётчик + поиск + кнопка фильтров), список тап-карточек, FAB «+».
 * Фильтры — в bottom-sheet (MobileFilterSheet).
 */

import {useState} from 'react';
import {Plus, SlidersHorizontal} from 'lucide-react';

import {type ClientListItem} from '@services/clients';
import {SearchField} from '@components/SearchField';
import {StatusPill} from '@components/StatusPill';
import {Loader} from '@components/ui/Loader';
import {formatNumber} from '@utils/format';

import {activeFilterCount, type ClientsFilters} from '../Clients.utils';
import {ClientTypeIcon} from '../ClientTypeIcon';
import {MobileFilterSheet} from '../MobileFilterSheet';

type ClientsMobileProps = {
  filters: ClientsFilters;
  patch: (p: Partial<ClientsFilters>) => void;
  rows: ClientListItem[];
  totalCount: number;
  isLoading: boolean;
  onOpenClient: (id: string) => void;
  onCreate: () => void;
};

export const ClientsMobile = ({
  filters,
  patch,
  rows,
  totalCount,
  isLoading,
  onOpenClient,
  onCreate,
}: ClientsMobileProps) => {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const activeCount = activeFilterCount(filters);

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-10 bg-background px-5 pb-3.5 pt-3">
        <h1 className="text-[24px] font-bold -tracking-[.02em]">Клиенты</h1>
        <div className="mt-0.5 text-[12.5px] text-muted-foreground">
          <span className="font-mono">{formatNumber(totalCount)}</span> клиентов
        </div>
        <div className="mt-3.5 flex gap-2.5">
          <SearchField
            value={filters.search}
            onChange={(v) => patch({search: v, page: 1})}
            placeholder="Название, имя, телефон…"
            className="h-[46px] min-w-0 flex-1 rounded-xl border border-input bg-card px-[13px]"
          />
          <button
            onClick={() => setFiltersOpen(true)}
            className="relative grid size-[46px] shrink-0 place-items-center rounded-xl border border-input bg-card"
            aria-label="Фильтры"
          >
            <SlidersHorizontal className="size-5 text-skr-ink" strokeWidth={1.8} />
            {activeCount > 0 && (
              <span className="absolute -right-1 -top-1 grid size-[18px] place-items-center rounded-full bg-primary text-[10px] font-semibold text-primary-foreground">
                {activeCount}
              </span>
            )}
          </button>
        </div>
      </header>

      <div className="flex flex-col gap-2 px-4 pb-24 pt-1">
        {isLoading ? (
          <div className="flex min-h-[420px] items-center justify-center">
            <Loader label="Загружаем клиентов…" />
          </div>
        ) : (
          rows.map((c) => (
            <button
              key={c.id}
              onClick={() => onOpenClient(c.id)}
              className="flex items-center gap-3 rounded-[14px] border border-skr-line2 bg-card px-3.5 py-[13px] text-left"
            >
              <ClientTypeIcon type={c.type} size={42} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-[15px] font-semibold">{c.name}</div>
                <div className="mt-0.5 truncate text-[12.5px] text-muted-foreground">
                  {c.cityName} · <span className="font-mono">{c.adminPhone}</span>
                </div>
              </div>
              <StatusPill status={c.subscriptionStatus} />
            </button>
          ))
        )}
        {!isLoading && rows.length === 0 && (
          <div className="py-16 text-center text-sm text-muted-foreground">Ничего не найдено</div>
        )}
      </div>

      <button
        onClick={onCreate}
        className="fixed bottom-6 right-5 grid size-[58px] place-items-center rounded-full bg-primary text-primary-foreground shadow-[0_8px_22px_rgba(44,92,219,.45)]"
        aria-label="Создать аккаунт"
      >
        <Plus className="size-6" strokeWidth={2.2} />
      </button>

      <MobileFilterSheet
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        filters={filters}
        patch={patch}
        totalCount={totalCount}
      />
    </div>
  );
};
